import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./EAModal.scss";
import { IoMdClose } from "react-icons/io";
import EAIconButton from "../Buttons/EAIconButton";
import EAButton from "../Buttons/EAButton";
import { useTranslation } from "react-i18next";
import EASkeletonLoader from "../EASkeletonLoader";
import useEAModalIframeScrolling from "./useEAModalIframeScrolling";
import useParentMessage from "../../../../utils/hooks/useParentMessage";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onAccept?: () => void;
  hideButtons?: boolean;
  cancelButtonLabel?: string;
  acceptButtonLabel?: string;
  hideAcceptButton?: boolean;
  loading?: boolean;
  isCloseButton?: boolean;
  desktopWidth?: string;
}

const EAModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  title,
  children,
  onAccept,
  hideButtons = false,
  cancelButtonLabel = "",
  acceptButtonLabel = "",
  hideAcceptButton = false,
  loading = false,
  isCloseButton = true,
  desktopWidth,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const { modalTop, maxHeight } = useEAModalIframeScrolling(modalRef);
  const { updateScrollPosition } = useParentMessage();
  const isDesktop = useDesktopSize();

  // Blokujemy przewijanie tła
  useEffect(() => {
    if (isOpen) {
      updateScrollPosition();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
    //eslint-disable-next-line
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="eamodal-overlay"
      onClick={(e) => {
        if ((e.target as HTMLElement).classList.contains("eamodal-overlay")) {
          onClose();
        }
      }}
    >
      <div
        className="eamodal-container"
        ref={modalRef}
        style={{
          position: "absolute",
          top: modalTop,
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: maxHeight,
          ...(desktopWidth && isDesktop ? { width: desktopWidth } : {}),
        }}
      >
        <div className="eamodal-header">
          <h5>{title}</h5>
          {isCloseButton && <EAIconButton title="Zamknij" icon={<IoMdClose />} onClick={onClose} variant="icon" />}
        </div>
        {loading ? (
          <EASkeletonLoader width="100%" height="200px" />
        ) : (
          <>
            <div className="eamodal-body">{children}</div>
            {!hideButtons && (
              <div className="eamodal-footer">
                <EAButton variant="outlined" title={cancelButtonLabel || t("anuluj")} onClick={onClose} />
                {!hideAcceptButton && <EAButton title={acceptButtonLabel || t("akceptuj")} onClick={onAccept} />}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EAModal;
